// Direct ESM import — Vitest's vi.mock() hoisting intercepts this correctly.
import { useDiagramStore } from '../stores/diagramStore';
import type { Node, Edge, NodeGroup, Layer, NodeMeta, SequenceParticipant, SequenceMessage, SequenceFragment, SequenceParticipantGroup } from '../types';

/**
 * Minimal slice of DiagramStore state + actions that services need.
 * Services depend on this interface, not on the concrete Zustand store,
 * satisfying the Dependency Inversion Principle and enabling unit testing
 * without spinning up the full store.
 */
export interface StoreAccessor {
  getState: () => DiagramSlice;
}

export interface DiagramSlice {
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  layers: Layer[];
  selectedItems: string[];
  activeLayerId: string;
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];

  // mutations
  addNode: (node: Omit<Node, 'id'>) => void;
  updateNode: (id: string, updates: Partial<Node>) => void;
  removeNode: (id: string) => void;
  addEdge: (edge: Omit<Edge, 'id'>) => void;
  updateEdge: (id: string, updates: Partial<Edge>) => void;
  removeEdge: (id: string) => void;
  selectItem: (id: string, multiSelect?: boolean) => void;
  clearSelection: () => void;
  addLayer: (name: string, color: string) => void;
  removeLayer: (id: string) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  setActiveLayer: (id: string) => void;
  setNodeLayer: (nodeId: string, layerId: string | undefined) => void;
  updateNodeMeta: (nodeId: string, meta: Partial<NodeMeta>) => void;
}

/**
 * Build a StoreAccessor that wraps the live Zustand diagramStore.
 * Called once at module init time (singleton pattern).
 */
export function makeLiveAccessor(): StoreAccessor {
  return {
    getState: () => useDiagramStore.getState() as unknown as DiagramSlice,
  };
}
